import type { ForecastRow } from '@contracts';

/**
 * Calendario de resurtido (predicción). Por producto × almacén:
 * velocidad de consumo, días de cobertura, fecha de quiebre, fecha sugerida
 * de pedido y cuánto pedir. El corazón del "¿cada cuándo resurtir?".
 */
export function ForecastTableWidget({ data }: { data: ForecastRow[] }) {
  const rows = Array.isArray(data) ? data : [];
  if (!rows.length) {
    return <div className="grid h-full place-items-center text-sm text-slate-400">Sin predicción</div>;
  }

  const dot = (s?: string) =>
    s === 'critical' ? 'bg-red-500' : s === 'warn' ? 'bg-amber-400' : 'bg-emerald-500';

  // "en 5 días" / "hoy" / "tarde 3 días" según reorderInDays.
  const when = (d?: number | null) => {
    if (d == null) return '—';
    if (d < 0) return `tarde ${Math.abs(d)}d`;
    if (d === 0) return 'hoy';
    return `en ${d}d`;
  };

  return (
    <div className="h-full overflow-auto pr-1">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white">
          <tr className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
            <th className="py-1 text-left">Producto</th>
            <th className="py-1 text-left">Almacén</th>
            <th className="py-1 text-right">Stock</th>
            <th className="py-1 text-right" title="Salida diaria: unidades que salen del almacén por día (promedio 8 semanas). Es una tasa, por eso lleva decimales — no es inventario.">Salida/día</th>
            <th className="py-1 text-right">Cobert.</th>
            <th className="py-1 text-left">Se quiebra</th>
            <th className="py-1 text-left">Pedir</th>
            <th className="py-1 text-right">Cant.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            // key compuesta: el forecast es por producto × ubicación → el id de
            // producto se repite entre almacenes.
            <tr key={`${r.id ?? 'x'}-${r.location ?? i}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
              <td className="py-1.5">
                <span className="flex items-center gap-1.5">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${dot(r.status)}`} />
                  <span className="truncate text-slate-700">{r.name}</span>
                </span>
                {r.sku && <span className="block pl-3.5 text-[10px] text-slate-400">{r.sku}</span>}
              </td>
              <td className="py-1.5 text-slate-500">{r.pos ?? '—'}</td>
              <td className={`py-1.5 text-right tabular-nums font-semibold ${r.stock <= 0 ? 'text-red-600' : 'text-slate-700'}`}>
                {r.stock}
              </td>
              <td className="py-1.5 text-right tabular-nums text-slate-500">{r.velocityDay ?? '—'}</td>
              <td className="py-1.5 text-right tabular-nums text-slate-500">
                {r.daysCover != null ? `${r.daysCover}d` : '—'}
              </td>
              <td className="py-1.5 whitespace-nowrap text-slate-600">{r.breakDate ?? '—'}</td>
              <td className="py-1.5 whitespace-nowrap">
                <span className="text-slate-700">{r.reorderDate ?? '—'}</span>
                <span className={`ml-1 text-[10px] ${(r.reorderInDays ?? 1) <= 0 ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
                  ({when(r.reorderInDays)})
                </span>
              </td>
              <td className="py-1.5 text-right tabular-nums font-bold text-blue-600">{r.units ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
